if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        // Zoom levels as width multipliers applied to the active media.
        this.zoomLevels = [1, 1.5, 2, 3];
        this.currentZoomIndex = 0;
        this._panState = null;

        // Zoom controls (event delegation so it survives re-renders).
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-zoom]');
          if (!btn) return;
          const action = btn.dataset.zoom;
          if (action === 'in') this.zoomIn();
          else if (action === 'out') this.zoomOut();
          else if (action === 'reset') this.resetZoom();
        });

        // Mouse drag-to-pan when zoomed (touch uses native scrolling).
        this.addEventListener('pointerdown', (e) => this._onPointerDown(e));
      }

      get content() {
        return this.querySelector('[role="document"]');
      }

      get activeMedia() {
        return (
          this.querySelector('[data-media-id].active') ||
          this.querySelector('.product-media-modal__content img')
        );
      }

      _onPointerDown(e) {
        if (this.currentZoomIndex === 0) return; // not zoomed
        if (e.pointerType && e.pointerType !== 'mouse') return; // touch/pen: native scroll
        const content = this.content;
        if (!content || !content.contains(e.target)) return;
        if (e.button !== 0) return;
        e.preventDefault(); // avoid native image drag ghost

        this._panState = {
          x: e.clientX,
          y: e.clientY,
          scrollLeft: content.scrollLeft,
          scrollTop: content.scrollTop,
        };

        const move = (ev) => {
          if (!this._panState) return;
          content.scrollLeft = this._panState.scrollLeft - (ev.clientX - this._panState.x);
          content.scrollTop = this._panState.scrollTop - (ev.clientY - this._panState.y);
        };
        const up = () => {
          this._panState = null;
          content.removeEventListener('pointermove', move);
          content.removeEventListener('pointerup', up);
          content.removeEventListener('pointercancel', up);
        };
        content.setPointerCapture?.(e.pointerId);
        content.addEventListener('pointermove', move);
        content.addEventListener('pointerup', up);
        content.addEventListener('pointercancel', up);
      }

      applyZoom() {
        const el = this.activeMedia;
        const content = this.content;
        const zoom = this.zoomLevels[this.currentZoomIndex];

        if (el) {
          if (zoom > 1) {
            el.style.width = `${100 * zoom}%`;
            el.style.maxWidth = 'none';
          } else {
            el.style.width = '';
            el.style.maxWidth = '';
          }
        }
        if (content) content.classList.toggle('is-zooming', zoom > 1);
        this._updateZoomButtons();
      }

      _updateZoomButtons() {
        const inBtn = this.querySelector('[data-zoom="in"]');
        const outBtn = this.querySelector('[data-zoom="out"]');
        const resetBtn = this.querySelector('[data-zoom="reset"]');
        const atMin = this.currentZoomIndex === 0;
        const atMax = this.currentZoomIndex === this.zoomLevels.length - 1;
        if (outBtn) outBtn.disabled = atMin;
        if (resetBtn) resetBtn.disabled = atMin;
        if (inBtn) inBtn.disabled = atMax;
      }

      zoomIn() {
        if (this.currentZoomIndex < this.zoomLevels.length - 1) {
          this.currentZoomIndex++;
          this.applyZoom();
        }
      }

      zoomOut() {
        if (this.currentZoomIndex > 0) {
          this.currentZoomIndex--;
          this.applyZoom();
        }
      }

      resetZoom() {
        this.currentZoomIndex = 0;
        this.applyZoom();
      }

      hide() {
        super.hide();
        this.resetZoom();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        // Reset any previous zoom each time the modal opens / switches media.
        this.resetZoom();
      }
    }
  );
}
